import { callGraphQL } from '../utils/request'
import {
  allPlayerInjuriesQuery,
  fetchTeamLeaderByStat,
  playerProfileDataBySlugQuery,
  playerFavoritesQuery,
  allPlayerTransactionsQuery,
  fetchPlayersWithContract,
  fetchPlayersOnTeamWithStatsSummary,
  playerInfoAndStatsByTeamQuery,
  playerSeasonStatsBySlugQuery,
  allFreeAgentsForYearQuery,
  statLeadersQuery,
  playerGameStatsBySlugQuery,
} from './player.queries'
import type {
  TeamResponse,
  PlayerResponse,
  AllPlayersResponse,
  TeamLeaderResponse,
  PlayerTransactionsResponse,
  PlayersContractResponse,
  PlayersOnTeamResponse,
  PlayerDataGQL,
  FreeAgent,
  FreeAgentResponse,
  LeagueLeadersResponse,
  LeagueLeader,
  TeamRoster,
} from './player.types'

/**
 * Fetch player profile data by name
 */
export const fetchPlayerProfileBySlugGQL = async (
  slug: string,
  season: number,
  statsYear: number,
  eventYear: number
): Promise<PlayerDataGQL | null> => {
  try {
    const playerData = await callGraphQL<PlayerResponse>(playerProfileDataBySlugQuery, {
      slug: slug,
      year: season,
      statsYear,
      eventYear,
    })
    if (!playerData?.player) return null
    return playerData.player
  } catch (err: any) {
    return null
  }
}

/**
 * Updating a player's season stats on year change
 */
export function updatePlayerSeasonStats(
  year: number,
  slug: string,
  statsYear: number,
  statSegment: string,
  eventYear: number
): Promise<PlayerResponse> {
  return callGraphQL<PlayerResponse>(playerSeasonStatsBySlugQuery, {
    slug: slug,
    year: year,
    statsYear: statsYear,
    statSegment: statSegment,
    // eventYear: eventYear,
  })
}

/**
 * Updating a player's game stats
 */
export function fetchPlayerEventStats(
  year: number,
  slug: string,
  includeCS: boolean = false
): Promise<PlayerResponse> {
  return callGraphQL<PlayerResponse>(playerGameStatsBySlugQuery, {
    slug: slug,
    year: year,
    includeCS: includeCS,
  })
}

/**
 * Fetch all players with stats for given team
 */
export async function fetchPlayersWithStatsByTeamGQL(
  id: string,
  year: number,
  statsYear: number,
  seasonSegment: string,
  includeCS: boolean = false
): Promise<TeamRoster | null> {
  try {
    const teamRes = await callGraphQL<TeamResponse | null>(
      playerInfoAndStatsByTeamQuery,
      {
        id: id,
        year: year,
        statsYear: statsYear,
        seasonSegment: seasonSegment,
        includeCS,
      }
    )
    if (!teamRes?.team) return null
    return teamRes.team
  } catch (error) {
    return null
  }
}

/**
 * Fetch all players for given team
 */
export function fetchAllPlayerForInjuries(
  statType: string,
  season: number
): Promise<AllPlayersResponse> | null {
  try {
    return callGraphQL<AllPlayersResponse>(allPlayerInjuriesQuery, {
      statType: statType,
      season: season,
    })
  } catch (error) {
    return null
  }
}

/**
 * Fetch team leaders by stat
 */
export async function fetchLeaderByStatList(
  year: number,
  limit: number,
  seasonSegment: string,
  statList: string[]
): Promise<LeagueLeader[]> {
  try {
    const leaderRes = await callGraphQL<LeagueLeadersResponse>(statLeadersQuery, {
      year,
      limit,
      seasonSegment,
      statList,
    })
    if (!leaderRes?.playerStatLeaders) return []
    return leaderRes.playerStatLeaders
  } catch (error) {
    return []
  }
}

/**
 * Fetch team leaders by stat
 */
export async function fetchLeaderByStatGQL(
  stat: string,
  year: number,
  statType: string,
  limit: number,
  teamId?: string
): Promise<TeamLeaderResponse | null> {
  try {
    return callGraphQL<TeamLeaderResponse>(fetchTeamLeaderByStat, {
      teamId: teamId,
      sortBy: stat,
      season: year,
      statType: statType,
      limit: limit,
    })
  } catch (error) {
    return null
  }
}

/**
 * Fetch default player that a user favorited by slug
 */
export async function fetchPlayerFavorites(
  slugList: string[],
  segment?: string
): Promise<PlayerDataGQL[]> {
  try {
    const playerRes = await callGraphQL<AllPlayersResponse>(playerFavoritesQuery, {
      slugList,
      segment: segment || 'regular',
    })
    return playerRes.allPlayers || []
  } catch (err) {
    return []
  }
}

/**
 * Fetch all player transactions
 */
export async function fetchAllPlayerTransactions(): Promise<PlayerTransactionsResponse | null> {
  try {
    const transactionsRes = await callGraphQL<PlayerTransactionsResponse>(
      allPlayerTransactionsQuery
    )
    return transactionsRes
  } catch (err) {
    return null
  }
}

/**
 * Fetch all players for league contracts table
 */
export async function fetchAllPlayersWithContract(
  year: number,
  status: string
): Promise<PlayersContractResponse | null> {
  try {
    const playersRes = await callGraphQL<PlayersContractResponse>(fetchPlayersWithContract, {
      status,
      year,
    })
    return playersRes
  } catch (err) {
    return null
  }
}

/**
 * Fetch all players that are on a team, includes statsSummary field
 * Used for all star voting
 */
export async function fetchPlayersOnTeam(
  year: number
): Promise<PlayersOnTeamResponse | null> {
  try {
    const playersRes = await callGraphQL<PlayersOnTeamResponse>(
      fetchPlayersOnTeamWithStatsSummary,
      {
        year,
      }
    )
    return playersRes
  } catch (err) {
    return null
  }
}

/**
 * Fetch all players that are on a team, includes statsSummary field
 * Used for all star voting
 */
export async function fetchFreeAgentsForYear(year: number): Promise<FreeAgent[]> {
  try {
    const playersRes = await callGraphQL<FreeAgentResponse | null>(
      allFreeAgentsForYearQuery,
      {
        year,
      }
    )
    if (!playersRes) {
      return []
    }
    return playersRes.freeAgents
  } catch (err) {
    return []
  }
}
